import { StorageExclusionsSearchableKeywords, StorageIcapSearchableKeywords } from './storage-protection.types';
import { translateFn } from '../../../../../../utils/translator.helper';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { ColumnValue } from '../../../../../../grid/types/common';
import {
  ScanActions,
  StorageExclusionItemType,
  StorageProtectionExclusionsSection,
  StorageProtectionIcapSection,
} from '@nirvana/shared/dtos/types/policies/common';

export const STORAGE_PROTECTION_ICAP_DEFAULT_VALUE: StorageProtectionIcapSection = {
  enabled: false,
  serviceName: 'bdicap',
  enablePort: true,
  listenPort: 1344,
  enableSslPort: false,
  listenSslPort: 11344,
  archiveScan: {
    enabled: false,
    archiveMaxSize: 3,
    archiveMaxDepth: 2,
  },
  congestionControl: {
    mode: 1,
    maxConnections: 300,
  },
  scanAction: {
    defaultAction: 1,
  },
};
export const STORAGE_PROTECTION_EXCLUSIONS_DEFAULT_VALUE: StorageProtectionExclusionsSection = {
  enabled: false,
  exclusionItems: [],
};

export const MAX_PORT_VALUE = 65535;
export const MIN_PORT_VALUE = 1;

export const MIN_ARCHIVE_SIZE = 0;
export const MAX_ARCHIVE_SIZE = 4096;
export const MIN_ARCHIVE_DEPTH = 1;
export const MAX_ARCHIVE_DEPTH = 64;

export const MIN_CONGESTION_CONTROL_CONNECTIONS = 1;
export const MAX_CONGESTION_CONTROL_CONNECTIONS = 99999;

// scan action form options
export function getScanActionsFormOptions(): Array<SelectOption> {
  return [
    {
      title: translateFn('Deny access'),
      value: ScanActions.DENY_ACCESS,
    },
    {
      title: translateFn('Disinfect'),
      value: ScanActions.DISINFECT,
    },
  ];
}

// get selectable type values
export function getExclusionItemTypeFormOptions(): Array<SelectOption> {
  return [
    {
      title: getLabelByExclusionItemType(StorageExclusionItemType.HASH),
      value: StorageExclusionItemType.HASH,
    },
    {
      title: getLabelByExclusionItemType(StorageExclusionItemType.WILDCARD),
      value: StorageExclusionItemType.WILDCARD,
    },
  ];
}
// get exclusion item type grid values
export function getExclusionItemTypeGridValues(): Array<ColumnValue> {
  return [
    {
      label: getLabelByExclusionItemType(StorageExclusionItemType.HASH),
      value: StorageExclusionItemType.HASH,
    },
    {
      label: getLabelByExclusionItemType(StorageExclusionItemType.WILDCARD),
      value: StorageExclusionItemType.WILDCARD,
    },
  ];
}
// get label by exclusion item type value
export function getLabelByExclusionItemType(type: StorageExclusionItemType): string {
  switch (type) {
    case StorageExclusionItemType.HASH:
      return translateFn('Hash');
    case StorageExclusionItemType.WILDCARD:
      return translateFn('Wildcard');
  }
}

// searchable keywords
export function getStorageIcapSearchableKeywords(): { [type in StorageIcapSearchableKeywords]: SearchableKeyword } {
  return {
    [StorageIcapSearchableKeywords.StorageOnAccessScanning]: {
      translatedLabel: translateFn('ICAP on-access scanning'),
      label: 'ICAP on-access scanning',
    },
    [StorageIcapSearchableKeywords.StorageConnectionSettings]: {
      translatedLabel: translateFn('Connection settings'),
      label: 'Connection settings',
    },
    [StorageIcapSearchableKeywords.StorageProtection]: {
      translatedLabel: translateFn('Storage Protection'),
      label: 'Storage Protection',
    },
    [StorageIcapSearchableKeywords.StorageIcap]: {
      translatedLabel: translateFn('ICAP'),
      label: 'ICAP',
    },
    [StorageIcapSearchableKeywords.StorageServiceName]: {
      translatedLabel: translateFn('Service name'),
      label: 'Service name',
    },
    [StorageIcapSearchableKeywords.StorageNonSecurePort]: {
      translatedLabel: translateFn('Listen on non-secure port'),
      label: 'Listen on non-secure port',
    },
    [StorageIcapSearchableKeywords.StorageSecurePort]: {
      translatedLabel: translateFn('Listen on secure port'),
      label: 'Listen on secure port',
    },
    [StorageIcapSearchableKeywords.StorageArchiveScanningSettings]: {
      translatedLabel: translateFn('Archive scanning settings'),
      label: 'Archive scanning settings',
    },
    [StorageIcapSearchableKeywords.StorageScanArchive]: {
      translatedLabel: translateFn('Scan archive'),
      label: 'Scan archive',
    },
    [StorageIcapSearchableKeywords.StorageArchiveMaxSize]: {
      translatedLabel: translateFn('Archive maximum size'),
      label: 'Archive maximum size',
    },
    [StorageIcapSearchableKeywords.StorageArchiveMaxDepth]: {
      translatedLabel: translateFn('Archive maximum depth'),
      label: 'Archive maximum depth',
    },
    [StorageIcapSearchableKeywords.StorageCongestionControl]: {
      translatedLabel: translateFn('Congestion control'),
      label: 'Congestion control',
    },
    [StorageIcapSearchableKeywords.StorageDropConnection]: {
      translatedLabel: translateFn(
        'Automatically drop new connections on storage devices if Security Server is overloaded'
      ),
      label: 'Automatically drop new connections on storage devices if Security Server is overloaded',
    },
    [StorageIcapSearchableKeywords.StorageMaxConnections]: {
      translatedLabel: translateFn('Maximum number of connections on storage devices'),
      label: 'Maximum number of connections on storage devices',
    },
    [StorageIcapSearchableKeywords.StorageScanActions]: {
      translatedLabel: translateFn('Scan actions'),
      label: 'Scan actions',
    },
    [StorageIcapSearchableKeywords.StorageDefaultAction]: {
      translatedLabel: translateFn('Default action for infected files'),
      label: 'Default action for infected files',
    },
  };
}
export function getStorageExclusionsSearchableKeywords(): {
  [type in StorageExclusionsSearchableKeywords]: SearchableKeyword;
} {
  return {
    [StorageExclusionsSearchableKeywords.StorageExclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    ...getStorageExclusionsGridSearchableKeywords(),
    ...getStorageExclusionsGridItemSearchableKeywords(),
  };
}
export function getStorageExclusionsGridSearchableKeywords(): {
  [StorageExclusionsSearchableKeywords.StorageExport]: SearchableKeyword;
  [StorageExclusionsSearchableKeywords.StorageImport]: SearchableKeyword;
} {
  return {
    [StorageExclusionsSearchableKeywords.StorageExport]: {
      translatedLabel: translateFn('Export'),
      label: 'Export',
    },
    [StorageExclusionsSearchableKeywords.StorageImport]: {
      translatedLabel: translateFn('Import'),
      label: 'Import',
    },
  };
}
export function getStorageExclusionsGridItemSearchableKeywords(): {
  [StorageExclusionsSearchableKeywords.StorageWildcard]: SearchableKeyword;
  [StorageExclusionsSearchableKeywords.StorageHash]: SearchableKeyword;
} {
  return {
    [StorageExclusionsSearchableKeywords.StorageWildcard]: {
      translatedLabel: translateFn('Wildcard'),
      label: 'Wildcard',
    },
    [StorageExclusionsSearchableKeywords.StorageHash]: {
      translatedLabel: translateFn('Hash'),
      label: 'Hash',
    },
  };
}
